import { readStaffPayErrorMessage } from "./staffPayApi";
import { ExtraHoursPaidRow, HoursMonthRow, OwnStaffPayMonth, PayDataAvailability, Payment, StaffPayHours } from "../types";

export type QueryState<Data = unknown> = { status: "loading" | "error" | "success"; data?: Data; error?: unknown };

export const FINANCE_PAYMENTS_UNREADABLE_REASON = "Não foi possível ler os pagamentos no finance. Volte a entrar para tentar de novo.";

const READY: PayDataAvailability = { state: "ready" };

export const resolveQueriesAvailability = (queryStates: Array<QueryState>) => {
    const failedQuery = queryStates.find((queryState) => queryState.status === "error");
    if (failedQuery) {
        const unavailable: PayDataAvailability = { state: "unavailable", reason: readStaffPayErrorMessage(failedQuery.error) };
        return unavailable;
    }
    const isLoading = queryStates.some((queryState) => queryState.status === "loading");
    const availability: PayDataAvailability = isLoading ? { state: "loading" } : READY;
    return availability;
};

type HoursRowsParameters = {
    hasFinanceAccess: boolean;
    canReadFinancePayments: boolean;
    hasPeopleToAsk: boolean;
    financeQueries: Array<QueryState>;
    financeMonthRows: Array<HoursMonthRow>;
    financeExtraHoursPaidRows: Array<ExtraHoursPaidRow>;
    staffPayHoursQuery: QueryState<StaffPayHours>;
};

export const resolveHoursRows = ({
    hasFinanceAccess,
    canReadFinancePayments,
    hasPeopleToAsk,
    financeQueries,
    financeMonthRows,
    financeExtraHoursPaidRows,
    staffPayHoursQuery,
}: HoursRowsParameters) => {
    if (hasFinanceAccess) {
        const financeAvailability: PayDataAvailability = canReadFinancePayments
            ? resolveQueriesAvailability(financeQueries)
            : { state: "unavailable", reason: FINANCE_PAYMENTS_UNREADABLE_REASON };
        const financeHoursRows = { availability: financeAvailability, monthRows: financeMonthRows, extraHoursPaidRows: financeExtraHoursPaidRows };
        return financeHoursRows;
    }
    if (!hasPeopleToAsk) {
        const nobodyToAskRows = { availability: READY, monthRows: [], extraHoursPaidRows: [] };
        return nobodyToAskRows;
    }
    const staffPayHoursRows = {
        availability: resolveQueriesAvailability([staffPayHoursQuery]),
        monthRows: staffPayHoursQuery.data?.month_rows ?? [],
        extraHoursPaidRows: staffPayHoursQuery.data?.extra_hours_paid_rows ?? [],
    };
    return staffPayHoursRows;
};

type OwnMoneyParameters = {
    hasFinanceAccess: boolean;
    isOwnPage: boolean;
    ownStaffPayQuery: QueryState<OwnStaffPayMonth>;
};

export const resolveOwnMoney = ({ hasFinanceAccess, isOwnPage, ownStaffPayQuery }: OwnMoneyParameters) => {
    if (hasFinanceAccess || !isOwnPage) {
        const noOwnMoney = { availability: READY, payment: null, ownRemunerations: null, agreementsWithoutPayments: [] };
        return noOwnMoney;
    }
    const ownMonth = ownStaffPayQuery.data;
    const firstPaymentOfMonth = ownMonth?.payments[0];
    const ownMoney = {
        availability: resolveQueriesAvailability([ownStaffPayQuery]),
        payment: firstPaymentOfMonth ? (firstPaymentOfMonth as unknown as Payment) : null,
        ownRemunerations: ownMonth?.remunerations ?? null,
        agreementsWithoutPayments: ownMonth?.agreements_without_payments ?? [],
    };
    return ownMoney;
};
